import { NotificationType } from '@/app/common/app-config/filter-models/misc';
import { BusinessAccount } from '@/app/common/general-types/business-account.dto';
import { CampaignManagerDto } from '@/app/common/general-types/campaigns.dto';
import { UserVisitDto } from '@/app/common/general-types/users.dto';
import { FiltersGenericDto, UserService } from '@/app/services/user/user.service';
import { Component, effect, inject, Input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { DatePipe, SlicePipe } from '@angular/common';
import { ImageService } from '@/app/services/image/image.service';
import { BrandInsightsState } from '../../brand-insights.state';

@Component({
  selector: 'brand-ins-visits',
  imports: [DatePipe, SlicePipe],
  templateUrl: './visits.html',
  styleUrl: './visits.css',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
})
export class Visits implements OnChanges {
  @Input() brand: BusinessAccount | undefined;

  selectedBusinessUser = signal<CampaignManagerDto | undefined>(undefined);

  userFilters = signal<FiltersGenericDto>({});
  pageNumber = signal<number>(0);

  isLoading = signal<boolean>(false);

  userVisits = signal<UserVisitDto[]>([]);
  userVisitsCount = signal<number>(0);

  /** Services */
  private userService = inject(UserService);
  private toastNotificationService = inject(ToastNotificationService);
  public imageService = inject(ImageService);
  private brandState = inject(BrandInsightsState);
  /** Services - END */

  constructor() {
    effect(() => {
      const fromState = this.brandState.brand();
      if (fromState && fromState.id !== this.brand?.id) {
        this.brand = fromState;
        this.selectedBusinessUser.set(fromState.businessUsers?.[0]);
        this.initFiltersAndFetch(this.selectedBusinessUser()?.userAccount.firebaseUid as string);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['brand']) {
      this.selectedBusinessUser.set(this.brand?.businessUsers?.[0]);
      this.initFiltersAndFetch(this.selectedBusinessUser()?.userAccount.firebaseUid as string);
    }
  }

  onUserSelect(event: Event) {
    const uid = (event.target as HTMLSelectElement).value;
    const member = this.brand?.businessUsers?.find(
      u => u.userAccount.firebaseUid === uid && !u.isDeleted
    );
    this.selectedBusinessUser.set(member);
    this.pageNumber.set(0);
    this.userVisits.set([]);
    this.initFiltersAndFetch(uid);
  }

  loadMore() {
    const next = this.pageNumber() + 1;
    this.pageNumber.set(next);
    this.getUserVisits(this.userFilters(), next);
  }

  onScroll(event: Event) {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (nearBottom && this.hasMore() && !this.isLoading()) {
      this.loadMore();
    }
  }

  memberDisplayName(member: CampaignManagerDto): string {
    const u = member.userAccount;
    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    return name || u.email || '—';
  }

  hasMore(): boolean {
    return this.userVisits().length < this.userVisitsCount();
  }

  activeUsers(): CampaignManagerDto[] {
    return (this.brand?.businessUsers ?? []).filter(m => !m.isDeleted);
  }

  private initFiltersAndFetch(selectedUserId: string | null) {
    this.isLoading.set(true);
    this.userFilters.set({
      take: 25,
      skip: this.pageNumber(),
      sortField: 'dateVisited',
      sortOrder: 'DESC'
    });

    if (selectedUserId) {
      this.userFilters.update((prev) => ({ ...prev, userId: selectedUserId }));
    }

    this.getUserVisits(this.userFilters(), this.pageNumber());
  }

  getUserVisits(userFilters: FiltersGenericDto, page: number) {
    this.isLoading.set(true);

    userFilters.skip = page * 25;
    userFilters.take = 25;

    this.userService.getUserVisits(userFilters).pipe(
      tap((response: any) => {
        if (response) {
          if (!this.userVisits || page === 0) {
            this.userVisits.set(response.data);
          } else {
            this.userVisits.update((prev) => [
              ...prev,
              ...response.data.filter((visit: UserVisitDto) => !prev.some(
                x => x.id === visit.id
              ))
            ]);
          }

          this.userVisitsCount.set(response.count);
        }
      }),
      catchError(err => {
        console.error('🚨 Error fetching user visits:', err);
        this.toastNotificationService.alert({
          title: 'Error fetching user visits',
          message: 'Failed to retrieve user visits!',
          type: 'error',
          icon: 'error',
        });
        return of({count: 0, data: []});
      }),
      finalize(() => {
        this.isLoading.set(false);
      })
    ).subscribe();
  }
}
